import React, { ReactElement, ComponentProps } from 'react';

import PropTypes from 'prop-types';

import { omitKeys } from '@adsk/alloy-react-helpers';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import DefaultError from './layout/DefaultError';
import DefaultLoadMore from './layout/DefaultLoadMore';
import useLoadMoreData, { UseLoadMoreData } from './useLoadMoreData';

const defaultRenderLoading = () => <ProgressRing data-testid="loading" />;
const defaultRenderError = (props: ComponentProps<typeof DefaultError>) => (
  <DefaultError {...props} />
);
const defaultRenderLoadMore = (
  props: ComponentProps<typeof DefaultLoadMore>,
) => <DefaultLoadMore {...props} />;

export type LoadMoreDataProps = StylableComponent<HTMLDivElement> &
  UseLoadMoreData & {
    renderLoading?: () => ReactElement;
    renderError?: (
      props: Pick<ReturnType<typeof useLoadMoreData>, 'onRetry'>,
    ) => ReactElement;
    renderLoadMore?: (
      props: Pick<ReturnType<typeof useLoadMoreData>, 'onLoad' | 'inViewRef'>,
    ) => ReactElement;
  };

const LoadMoreData = ({
  style,
  renderLoading = defaultRenderLoading,
  renderError = defaultRenderError,
  renderLoadMore = defaultRenderLoadMore,
  ...props
}: LoadMoreDataProps): ReactElement => {
  const { isLoading, hasError, inViewRef, onLoad, onRetry } =
    useLoadMoreData<HTMLButtonElement>(props);
  return (
    <div
      css={[
        {
          display: 'flex',
          alignItems: 'center',
        },
        style,
      ]}
      {...omitKeys(props, [
        'isLoading',
        'hasError',
        'isAuto',
        'onLoad',
        'onRetry',
      ])}
    >
      {(() => {
        if (hasError) {
          return renderError({ onRetry });
        }
        if (isLoading) {
          return renderLoading();
        }
        return renderLoadMore({ inViewRef, onLoad });
      })()}
    </div>
  );
};

LoadMoreData.propTypes = {
  /* A callback function which is responsible for fetching more data to pump into the
   * table. It should return a promise that resolves after the data loading is complete.
   * Depending on whether `isAuto` is true or false, this callback will be triggered via
   * click or as soon as this component enters the viewport.
   */
  onLoad: PropTypes.func.isRequired,
  /* An optional callback function which will be used when an error is present.
   * Essentially onLoad is called initially, and if it fails, the onRetry callback will be
   * used. If onRetry is omitted, the onLoad function will do double duty and fill in the
   * onRetry gap.
   */
  onRetry: PropTypes.func,
  /* A flag to determine if a user click event is required to load more or if the
   * component should load more automatically (i.e. as soon as the component comes into
   * view).
   */
  isAuto: PropTypes.bool,
  /* An optional boolean flag which would allow consumers to control the error state
   * rather than depend on the internal error handling.
   */
  hasError: PropTypes.bool,
  /* An optional boolean flag which would allow consumers to control the loading state
   * rather than depend on the internal loading.
   */
  isLoading: PropTypes.bool,
  /* A function to render custom jsx for the loading state */
  renderLoading: PropTypes.func,
  /* A function to render custom jsx for the initial load more state */
  renderLoadMore: PropTypes.func,
  /* A function to render custom jsx for the error state */
  renderError: PropTypes.func,
  /* Styles applied to the root element */
  style: stylePropType,
  /* Class applied to the root element */
  className: PropTypes.string,
};

export default LoadMoreData;
